import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import ColumnDropdownSelector, {
  ColumnDropdownSelectorFilterOptions,
} from 'data-frames/column-selector/ColumnDropdownSelector';
import {GetPipelineConfigSpecType} from 'pipelines/StepShapeUtils';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {ButtonSize} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import {CornerRadius} from 'ui-library/dropdowns/SelectDropdown';

import {getDataFrameInfoOrEmptyInfo, useStepConfigEditorContext} from '../StepConfigEditorContext';

interface ColumnReferenceProps {
  config: string | null;
  onChangeConfig: (config: string | null) => void;
  configShape: GetPipelineConfigSpecType<'columnReference'>;
  dataTestAttrs?: DataTestAttrs;
  disabled?: boolean;
  buttonSize?: ButtonSize;
  cornerRadius?: CornerRadius;
  noColumnSelectedText?: string;
  allowSelectNull?: boolean;
  filterOptions?: ColumnDropdownSelectorFilterOptions;
  renderCyclingSelector?: boolean;
  isExpandable?: boolean;
  isExpanded?: boolean;
}

export default function ColumnReferenceEditor({
  config,
  onChangeConfig,
  configShape,
  dataTestAttrs,
  disabled,
  cornerRadius,
  buttonSize = ButtonSize.MEDIUM,
  noColumnSelectedText = 'Select column',
  allowSelectNull = false,
  filterOptions = undefined,
  renderCyclingSelector = false,
  isExpandable = false,
  isExpanded = false,
}: ColumnReferenceProps): JSX.Element {
  const {dataFramesInfos} = useStepConfigEditorContext();
  const {dataFrameSpec} = getDataFrameInfoOrEmptyInfo(dataFramesInfos, configShape.inputDataName);

  const effectiveCornerRadius = isExpandable
    ? {
        topLeft: 4,
        topRight: 0,
        bottomLeft: isExpanded ? 0 : 4,
        bottomRight: 0,
      }
    : cornerRadius;

  const buttonClassName = isExpandable ? css(styles.expandable, isExpanded && styles.expanded) : undefined;

  return (
    <ColumnDropdownSelector
      dataFrameSpec={dataFrameSpec}
      dataTestAttrs={dataTestAttrs}
      onChange={onChangeConfig}
      selectedColumnSystemName={config}
      validationOptions={configShape.validationOptions ?? undefined}
      buttonSize={buttonSize}
      buttonClassName={buttonClassName}
      noColumnSelectedText={noColumnSelectedText}
      allowSelectNull={allowSelectNull}
      disabled={disabled}
      cornerRadius={effectiveCornerRadius}
      filterOptions={filterOptions}
      renderCyclingSelector={renderCyclingSelector}
      displayMissingColumnName={true}
    />
  );
}

const styles = StyleSheet.create({
  expandable: {
    border: `1px solid ${Colors.GRAY_LIGHT}`,
    borderRight: 'none',
  },
  expanded: {
    borderBottomWidth: '0',
  },
});
